import { Track } from 'livekit-client';
import React, { useCallback, useEffect, useRef } from 'react';
import { registerParticipantVideoElement, unregisterParticipantVideoElement } from 'hooks/videoElementRegistry';
import { VideoItem } from './view';

export interface VideoRendererProps {
    track: Track;
    isLocal: boolean;
    className?: string;
    onSizeChanged?: (width: number, height: number) => void;
    participantId?: string;
    onVideoElementReady?: (element: HTMLVideoElement) => void;
}

export const VideoRenderer = ({
    track,
    isLocal,
    onSizeChanged,
    className,
    participantId,
    onVideoElementReady,
}: VideoRendererProps) => {
    const ref = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) {
            return;
        }
        el.muted = true;
        track.attach(el);
        if (participantId) {
            el.dataset.participantId = participantId.trim();
            registerParticipantVideoElement(participantId, el);
        }
        el.dataset.trackSid = track.sid ?? '';

        onVideoElementReady?.(el);

        return () => {
            if (participantId) {
                unregisterParticipantVideoElement(participantId, el);
            }
            track.detach(el);
            delete el.dataset.participantId;
            delete el.dataset.trackSid;
        };
    }, [track, participantId, onVideoElementReady]);

    const handleResize = useCallback(
        (ev: UIEvent) => {
            if (ev.target instanceof HTMLVideoElement) {
                onSizeChanged?.(ev.target.videoWidth, ev.target.videoHeight);
            }
        },
        [onSizeChanged],
    );

    useEffect(() => {
        const el = ref.current;
        if (el) {
            el.addEventListener('resize', handleResize);
        }
        return () => {
            el?.removeEventListener('resize', handleResize);
        };
    }, [handleResize]);

    const facingMode = track.mediaStreamTrack?.getSettings().facingMode;
    // Зеркалим локальное видео, если это не задняя камера (environment)
    const shouldMirror = isLocal && facingMode !== 'environment';

    return <VideoItem ref={ref} className={className} $isReflected={shouldMirror} />;
};
